import React from "react";
import { Link } from "react-router-dom";
import BookLink from "./BookLink";
import { Instagram, Facebook, Mail, Phone, MapPin, Heart, Star } from "lucide-react";
import Logo from "./Logo";
import { business, nav } from "../data/content";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-pale-rose/50 pt-20 pb-10 mt-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2 max-w-md">
          <Logo className="h-28 sm:h-36 md:h-44 w-auto" />
          <p className="mt-6 font-body text-charcoal-soft leading-relaxed">
            {business.tagline}
          </p>
          <p className="mt-3 font-body text-sm text-charcoal-soft">
            Inclusive, personalized permanent hair removal in {business.city}, {business.state}.
          </p>
          <BookLink  className="btn-petal mt-8" data-testid="footer-book-btn">
            Book Your Appointment
          </BookLink>
        </div>

        <div>
          <h3 className="font-serif-display text-xl mb-5">Explore</h3>
          <ul className="space-y-2.5 font-body text-sm text-charcoal-soft">
            {nav.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="hover:text-petal transition-colors duration-200"
                  data-testid={`footer-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <BookLink  className="hover:text-petal transition-colors duration-200">
                Book Now
              </BookLink>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-serif-display text-xl mb-5">Get in Touch</h3>
          <ul className="space-y-3 font-body text-sm text-charcoal-soft">
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="text-petal mt-0.5 shrink-0" />
              <span>{business.address}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Phone size={16} className="text-petal mt-0.5 shrink-0" />
              <a href={business.phoneHref} className="hover:text-petal" data-testid="footer-phone-link">
                {business.phone}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={16} className="text-petal mt-0.5 shrink-0" />
              <a href={business.emailHref} className="hover:text-petal" data-testid="footer-email-link">
                {business.email}
              </a>
            </li>
          </ul>

          <div className="flex items-center gap-3 mt-6" aria-label="Social media">
            <a
              href={business.social.instagram}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/70 text-petal hover:bg-petal hover:text-white transition-colors duration-200"
              data-testid="social-instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href={business.social.facebook}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Facebook"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/70 text-petal hover:bg-petal hover:text-white transition-colors duration-200"
              data-testid="social-facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href={business.social.googleMaps}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Google Business Profile"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/70 text-petal hover:bg-petal hover:text-white transition-colors duration-200"
              data-testid="social-google"
            >
              <Star size={16} />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 mt-14 pt-8 border-t border-white/60 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-body text-xs text-charcoal-soft flex items-center gap-1.5">
          © {year} {business.name} · Made with
          <Heart size={12} className="text-petal fill-petal" />
          in {business.city}, {business.state}
        </p>
        <div className="flex gap-6 font-body text-xs text-charcoal-soft">
          <Link to="/legal/privacy" className="hover:text-petal" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/legal/terms" className="hover:text-petal" data-testid="footer-terms">Terms</Link>
          <Link to="/legal/accessibility" className="hover:text-petal" data-testid="footer-accessibility">Accessibility</Link>
        </div>
      </div>
    </footer>
  );
}
